import { Link } from "react-router-dom";
import { useChatActions } from "./ChatActionContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BookOpen, Calendar, ClipboardList, GraduationCap, Users, Sparkles, Puzzle, PenTool, ArrowRight } from "lucide-react";
import { InlineTestRunner } from "./inline/InlineTestRunner";
import { InlineCalendar } from "./inline/InlineCalendar";
import { InlineGames } from "./inline/InlineGames";
import { InlineWorksheet } from "./inline/InlineWorksheet";
import { InlineWriting } from "./inline/InlineWriting";
import { CoursePlanCard, CourseListCard, ProgressReportCard, HistoryRecallCard, StepUpdateCard } from "./cards/CourseCards";


export function ToolResultRenderer({ name, output }: { name: string; output: any }) {
  if (!output) return null;

  if (output.error === "tool_failed") {
    return (
      <Card><CardContent className="p-4 text-sm text-muted-foreground">
        I couldn't load that just now. Please try again in a moment.
      </CardContent></Card>
    );
  }

  switch (name) {

    case "run_practice_test":
    case "run_ai_practice_test":
    case "run_mixed_practice_test":
      if (output.needsInput) return <Card><CardContent className="p-4 text-sm">{output.message}</CardContent></Card>;
      return <InlineTestRunner spec={output} />;
    case "run_games":
      if (output.needsInput) return <Card><CardContent className="p-4 text-sm">{output.message}</CardContent></Card>;
      return <InlineGames spec={output} />;
    case "run_worksheet":
      if (output.needsInput) return <Card><CardContent className="p-4 text-sm">{output.message}</CardContent></Card>;
      return <InlineWorksheet spec={output} />;
    case "run_writing_pad":
      if (output.needsInput) return <Card><CardContent className="p-4 text-sm">{output.message}</CardContent></Card>;
      return <InlineWriting spec={output} />;
    case "show_calendar":
      if (output.error === "sign_in_required") break;
      return <InlineCalendar role={output.role} />;
    case "get_my_progress":
      if (output.error === "sign_in_required") break;
      if (output.error) break;
      return output.render === "progress_report"
        ? <ProgressReportCard data={output} />
        : <ProgressCard data={output} />;
    case "build_learning_plan":
      return <PlanCard data={output} />;
    case "create_learning_path":
    case "get_learning_path":
      if (output.error) break;
      return <CoursePlanCard data={output} />;
    case "list_learning_paths":
      if (output.error) break;
      return <CourseListCard paths={output.paths ?? []} />;
    case "complete_path_step":
      if (output.error) break;
      return <StepUpdateCard data={output} />;
    case "get_progress_report":
      if (output.error === "sign_in_required") break;
      if (output.needsInput) return <Card><CardContent className="p-4 text-sm">Which child did you mean — {(output.options ?? []).join(", ")}?</CardContent></Card>;
      return <ProgressReportCard data={output} />;
    case "recall_history":
      if (output.error === "sign_in_required") break;
      return <HistoryRecallCard data={output} />;
    case "remember_about_user":
    case "forget_about_user":
      return null;


    case "list_grades":
      return <GradePicker grades={output.grades ?? []} />;
    case "list_subjects":
      return <SubjectList grade={output.grade} subjects={output.subjects ?? []} needsInput={output.needsInput} message={output.message} />;
    case "list_categories":
      return <CategoryList grade={output.grade} subject={output.subject} categories={output.categories ?? []} needsInput={output.needsInput} message={output.message} />;
    case "suggest_practice":
      return <PracticeSuggestions suggestions={output.suggestions ?? []} />;
    case "get_student_dashboard":
      return <StudentDashboardCard data={output} />;
    case "get_parent_dashboard":
      return <ParentDashboardCard data={output} />;
    case "get_tutor_dashboard":
      return <TutorDashboardCard data={output} />;
    case "start_practice_test":
    case "start_ai_practice_test":
      if (output.needsInput) {
        return <Card><CardContent className="p-4 text-sm">{output.message}</CardContent></Card>;
      }
      return <InlineTestRunner spec={output} />;
    case "find_games":
      if (output.needsInput) return <Card><CardContent className="p-4 text-sm">{output.message}</CardContent></Card>;
      return <InlineGames spec={output} />;
    case "find_worksheets":
      if (output.needsInput) return <Card><CardContent className="p-4 text-sm">{output.message}</CardContent></Card>;
      return <InlineWorksheet spec={output} />;
    case "open_writing_pad":
      if (output.needsInput) return <Card><CardContent className="p-4 text-sm">{output.message}</CardContent></Card>;
      return <InlineWriting spec={output} />;
    case "open_calendar":
      if (output.error === "sign_in_required") break;
      return <InlineCalendar role={output.role ?? "student"} />;
    case "open_page":
      return <AskButton prompt={output.prompt ?? `Open ${output.label ?? output.href} here in the chat`} label={output.label ?? "Open"} />;
  }

  if (output.error === "sign_in_required") {
    return (
      <Card><CardContent className="p-4 flex items-center justify-between gap-3">
        <span className="text-sm">Sign in to use this feature.</span>
        <Button asChild size="sm"><Link to="/auth">Sign in</Link></Button>
      </CardContent></Card>
    );
  }

  return null;
}

const KIND_META: Record<string, { icon: any; title: (d: any) => string; blurb: (d: any) => string; accent: string }> = {
  test: {
    icon: ClipboardList,
    title: (d) => `Grade ${d.grade} ${d.subject}${d.category ? " · " + d.category : ""} practice test`,
    blurb: () => "Curated questions from the question bank.",
    accent: "from-blue-500/15 to-blue-500/0 border-blue-500/30",
  },
  ai_test: {
    icon: Sparkles,
    title: (d) => `AI practice test${d.grade ? " · Grade " + d.grade : ""}${d.subject ? " · " + d.subject : ""}`,
    blurb: () => "Fresh questions generated for you (guests: 5 per 6 hours).",
    accent: "from-purple-500/15 to-purple-500/0 border-purple-500/30",
  },
  game: {
    icon: Puzzle,
    title: (d) => `Learning games · Grade ${d.grade}${d.subject ? " · " + d.subject : ""}`,
    blurb: () => "Play quick, focused mini-games.",
    accent: "from-emerald-500/15 to-emerald-500/0 border-emerald-500/30",
  },
  worksheet: {
    icon: BookOpen,
    title: (d) => `Worksheets · Grade ${d.grade}${d.subject ? " · " + d.subject : ""}`,
    blurb: () => "Printable and interactive worksheets.",
    accent: "from-orange-500/15 to-orange-500/0 border-orange-500/30",
  },
  writing: {
    icon: PenTool,
    title: (d) => `Writing pad · Grade ${d.grade}`,
    blurb: () => "Guided prompts with AI feedback.",
    accent: "from-pink-500/15 to-pink-500/0 border-pink-500/30",
  },
  calendar: {
    icon: Calendar,
    title: () => "Your calendar",
    blurb: () => "Sessions, tests, and reminders.",
    accent: "from-indigo-500/15 to-indigo-500/0 border-indigo-500/30",
  },
};

function LaunchCard({ data }: { data: any }) {
  const meta = KIND_META[data.kind];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <Card className={`bg-gradient-to-br ${meta.accent} border-2`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className="h-11 w-11 rounded-xl bg-background/70 flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{meta.title(data)}</div>
          <div className="text-xs text-muted-foreground">{meta.blurb(data)}</div>
        </div>
        <AskButton
          className="shrink-0"
          label={data.cta ?? "Open"}
          prompt={`Start this here in the chat: ${meta.title(data)}`}
        />
      </CardContent>
    </Card>
  );
}

function CategoryList({ grade, subject, categories, needsInput, message }: any) {
  if (needsInput) return <Card><CardContent className="p-4 text-sm">{message}</CardContent></Card>;
  if (!categories.length) {
    return <Card><CardContent className="p-4 text-sm text-muted-foreground">No categories listed for Grade {grade} {subject}.</CardContent></Card>;
  }
  return (
    <Card><CardContent className="p-4">
      <div className="text-sm font-medium mb-2 flex items-center gap-2">
        <ClipboardList className="h-4 w-4" /> Grade {grade} {subject} · pick a category
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {categories.map((c: any) => (
          <AskButton
            key={c.id}
            variant="outline"
            className="justify-start h-auto py-2 w-full"
            label={c.name}
            prompt={`Run a Grade ${grade} ${subject} practice test on ${c.name} here in the chat`}
          />
        ))}
      </div>
    </CardContent></Card>
  );
}


function GradePicker({ grades }: { grades: string[] }) {
  return (
    <Card><CardContent className="p-4">
      <div className="text-sm font-medium mb-2 flex items-center gap-2"><GraduationCap className="h-4 w-4" /> Pick a grade</div>
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
        {grades.map((g) => (
          <AskButton key={g} variant="outline" size="sm" label={`Gr ${g}`} prompt={`I'm in grade ${g} — show me the subjects here in the chat`} />
        ))}
      </div>
    </CardContent></Card>
  );
}

function SubjectList({ grade, subjects, needsInput, message }: any) {
  if (needsInput) {
    return (
      <Card><CardContent className="p-4 text-sm">{message ?? "I need a grade first."}</CardContent></Card>
    );
  }
  return (
    <Card><CardContent className="p-4">
      <div className="text-sm font-medium mb-2 flex items-center gap-2">
        <BookOpen className="h-4 w-4" /> Grade {grade} subjects
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {subjects.map((s: any) => (
          <AskButton
            key={s.id}
            variant="outline"
            className="justify-start w-full"
            label={s.name}
            prompt={`Show me Grade ${grade} ${s.name} categories here in the chat`}
          />
        ))}
        {subjects.length === 0 && (
          <div className="text-sm text-muted-foreground">No subjects listed for grade {grade}.</div>
        )}
      </div>
    </CardContent></Card>
  );
}

function PracticeSuggestions({ suggestions }: { suggestions: Array<{ type: string; label: string; href: string }> }) {
  const iconFor = (t: string) => {
    if (t === "test") return <ClipboardList className="h-4 w-4" />;
    if (t === "game") return <GraduationCap className="h-4 w-4" />;
    if (t === "worksheet") return <BookOpen className="h-4 w-4" />;
    return <BookOpen className="h-4 w-4" />;
  };
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {suggestions.map((s) => (
        <AskButton
          key={s.label}
          variant="outline"
          className="justify-start gap-2 h-auto py-3 w-full"
          icon={iconFor(s.type)}
          label={s.label}
          prompt={`Start this here in the chat: ${s.label}`}
        />
      ))}
    </div>
  );
}

function StudentDashboardCard({ data }: { data: any }) {
  return (
    <Card><CardContent className="p-4 space-y-3">
      <div className="text-sm font-medium">Your dashboard</div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <Stat label="Recent tests" value={data.recent_tests?.length ?? 0} />
        <Stat label="Upcoming" value={data.upcoming_sessions?.length ?? 0} />
        <Stat label="Invitations" value={data.pending_invitations?.length ?? 0} />
      </div>
      <div className="flex flex-wrap gap-2">
        <AskButton size="sm" variant="outline" label="Full dashboard" prompt="Show my dashboard summary here in the chat" />
        <AskButton size="sm" variant="outline" label="Calendar" prompt="Show my calendar here in the chat" />
        <AskButton size="sm" variant="outline" label="Analysis" prompt="Show my progress report here in the chat" />
      </div>
    </CardContent></Card>
  );
}

function ParentDashboardCard({ data }: { data: any }) {
  return (
    <Card><CardContent className="p-4 space-y-3">
      <div className="text-sm font-medium flex items-center gap-2"><Users className="h-4 w-4" /> Your children</div>
      {(data.children ?? []).length === 0 && (
        <div className="text-sm text-muted-foreground">No children linked yet.</div>
      )}
      <div className="grid gap-2">
        {(data.children ?? []).map((c: any) => (
          <div key={c.id} className="flex items-center justify-between rounded-md border p-2 text-sm">
            <div>
              <div className="font-medium">{c.student_name}</div>
              <div className="text-xs text-muted-foreground">Grade {c.grade} · {c.student_email}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <AskButton size="sm" variant="outline" label="Children progress" prompt="Show me a progress report for my children here in the chat" />
        <AskButton size="sm" variant="outline" label="Calendar" prompt="Show our family calendar here in the chat" />
      </div>
    </CardContent></Card>
  );
}

function TutorDashboardCard({ data }: { data: any }) {
  return (
    <Card><CardContent className="p-4 space-y-3">
      <div className="text-sm font-medium flex items-center gap-2"><Calendar className="h-4 w-4" /> Tutor overview</div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <Stat label="Tests" value={data.recent_tests?.length ?? 0} />
        <Stat label="Sessions" value={data.upcoming_schedules?.length ?? 0} />
        <Stat label="Lists" value={data.student_lists?.length ?? 0} />
      </div>
      <div className="flex flex-wrap gap-2">
        <AskButton size="sm" variant="outline" label="Overview" prompt="Show my tutor overview here in the chat" />
        <AskButton size="sm" variant="outline" label="Tests" prompt="Show my tests and student results here in the chat" />
        <AskButton size="sm" variant="outline" label="Calendar" prompt="Show my calendar here in the chat" />
      </div>
    </CardContent></Card>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border p-2">
      <div className="text-lg font-semibold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function ProgressCard({ data }: { data: any }) {
  const per = data.per_subject ?? [];
  return (
    <Card><CardContent className="p-4 space-y-3">
      <div className="text-sm font-medium">Your progress · last {data.window_days ?? 30} days</div>
      {per.length === 0 && <div className="text-sm text-muted-foreground">No completed tests yet — take one and I'll track it here.</div>}
      {per.length > 0 && (
        <div className="grid gap-2">
          {per.map((s: any) => (
            <div key={s.subject} className="flex items-center justify-between rounded-md border p-2 text-sm">
              <span className="font-medium">{s.subject}</span>
              <span className="text-muted-foreground">{s.avgPercent}% · {s.tests} test{s.tests === 1 ? "" : "s"}</span>
            </div>
          ))}
        </div>
      )}
      {data.weakest && data.strongest && data.weakest.subject !== data.strongest.subject && (
        <div className="text-xs text-muted-foreground">
          Strongest: <span className="font-medium text-foreground">{data.strongest.subject}</span> ·
          Focus area: <span className="font-medium text-foreground">{data.weakest.subject}</span>
        </div>
      )}
      {(data.recent ?? []).length > 0 && (
        <div className="pt-2 border-t">
          <div className="text-xs font-medium mb-1">Recent tests</div>
          <div className="grid gap-1">
            {data.recent.slice(0, 5).map((r: any) => (
              <div key={r.id} className="flex items-center justify-between text-xs">
                <span>{r.subject}</span>
                <span className="text-muted-foreground">{r.score}/{r.total} ({r.percent}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </CardContent></Card>
  );
}

const ACTIVITY_META: Record<string, { icon: any; label: string; accent: string }> = {
  practice_test:    { icon: ClipboardList, label: "Practice test",    accent: "bg-blue-500/10 text-blue-700 dark:text-blue-300" },
  ai_practice_test: { icon: Sparkles,      label: "AI practice test", accent: "bg-purple-500/10 text-purple-700 dark:text-purple-300" },
  worksheet:        { icon: BookOpen,      label: "Worksheet",        accent: "bg-orange-500/10 text-orange-700 dark:text-orange-300" },
  game:             { icon: Puzzle,        label: "Game",             accent: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" },
  writing:          { icon: PenTool,       label: "Writing",          accent: "bg-pink-500/10 text-pink-700 dark:text-pink-300" },
  review:           { icon: GraduationCap, label: "Review",           accent: "bg-slate-500/10 text-slate-700 dark:text-slate-300" },
};

function PlanCard({ data }: { data: any }) {
  const blocks: any[] = data.blocks ?? [];
  const byDay = blocks.reduce((acc: Record<string, any[]>, b) => {
    (acc[b.day_label] ??= []).push(b);
    return acc;
  }, {});
  const dayOrder = Array.from(new Set(blocks.map((b) => b.day_label)));

  const promptFor = (b: any) => {
    const g = data.grade;
    const subj = b.subject ?? "";
    const where = "here in the chat";
    switch (b.activity) {
      case "practice_test":
        return `Run a Grade ${g} ${subj} practice test${b.category ? ` on ${b.category}` : ""} ${where}`;
      case "ai_practice_test":
        return `Generate an AI practice test for Grade ${g} ${subj}${b.category ? ` on ${b.category}` : ""} ${where}`;
      case "worksheet": return `Open a Grade ${g} ${subj} worksheet ${where}`;
      case "game":      return `Open Grade ${g} ${subj} learning games ${where}`;
      case "writing":   return `Open the Grade ${g} writing pad ${where}`;
      case "review":    return `Show my progress report ${where}`;
      default:          return `Help me with this step: ${b.subject ?? ""} ${b.note ?? ""}`;
    }
  };

  return (
    <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardContent className="p-4 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">
            {data.role === "parent" ? "Family plan" : data.role === "instructor" ? "Session plan" : "Study plan"}
            {data.horizon ? ` · ${String(data.horizon).replace(/_/g, " ")}` : ""}
          </div>
          <div className="font-semibold text-base">{data.title}</div>
          {data.rationale && (
            <div className="text-sm text-muted-foreground mt-1">{data.rationale}</div>
          )}
          {(data.goal || data.minutes_per_day) && (
            <div className="text-xs text-muted-foreground mt-1">
              {data.goal && <>Goal: <span className="text-foreground">{data.goal}</span></>}
              {data.goal && data.minutes_per_day ? " · " : ""}
              {data.minutes_per_day ? <>~{data.minutes_per_day} min/day</> : null}
            </div>
          )}
        </div>

        <div className="space-y-3">
          {dayOrder.map((day) => (
            <div key={day}>
              <div className="text-xs font-semibold text-muted-foreground mb-1.5">{day}</div>
              <div className="grid gap-1.5">
                {byDay[day].map((b: any, i: number) => {
                  const meta = ACTIVITY_META[b.activity] ?? ACTIVITY_META.review;
                  const Icon = meta.icon;
                  return (
                    <div key={i} className="flex items-center gap-3 rounded-md border bg-background/60 p-2.5">
                      <div className={`h-8 w-8 rounded-md flex items-center justify-center ${meta.accent}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {b.subject}{b.category ? ` · ${b.category}` : ""}
                          <span className="ml-2 text-xs text-muted-foreground">{b.minutes} min · {meta.label}</span>
                        </div>
                        {b.note && <div className="text-xs text-muted-foreground truncate">{b.note}</div>}
                      </div>
                      <AskButton size="sm" variant="outline" className="shrink-0" label="Start" prompt={promptFor(b)} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


export function AskButton({
  prompt, label, icon, className, size = "sm", variant = "default",
}: {
  prompt: string;
  label: string;
  icon?: React.ReactNode;
  className?: string;
  size?: "sm" | "default" | "lg";
  variant?: "default" | "outline" | "secondary" | "ghost";
}) {
  const { ask } = useChatActions();
  return (
    <Button size={size} variant={variant} className={`gap-1 ${className ?? ""}`} onClick={() => ask(prompt)}>
      {icon}
      <span className="text-left">{label}</span>
      {!icon && <ArrowRight className="h-3.5 w-3.5" />}
    </Button>
  );
}
